import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, tap } from 'rxjs';
import { MenuDataStore, normalizeMenuRoute } from '../state/menu-data.store';

/**
 * Endpoints that are analytical read queries issued via POST
 * which can safely be recorded in state management per menu item.
 */
const CACHEABLE_POST_PATTERNS = [
  '/gemini-usage/admin-summary',
  '/gemini-usage/reportees-summary',
  '/gemini-usage/my-summary',
  '/gemini-usage/my-daily-usage',
  '/gemini-usage/license-data'
];

/**
 * Mutation / sync endpoints that should trigger cache invalidation
 * for the current or affected menu item.
 */
const INVALIDATION_PATTERNS = [
  '/gemini-usage/company-statistics/refresh',
  '/workspace-usage/sync',
  '/role-access/save',
  '/gars-users/update'
];

/** Header name to explicitly bypass cache and fetch fresh data from server */
export const BYPASS_CACHE_HEADER = 'X-Gars-Bypass-Cache';

function isCacheable(req: HttpRequest<unknown>): boolean {
  if (req.headers.has(BYPASS_CACHE_HEADER)) {
    return false;
  }

  // Never cache authentication, user profile, or role/permission requests
  if (
    req.url.includes('/auth/') ||
    req.url.includes('/gars-users/') ||
    req.url.includes('/role-access/') ||
    req.url.includes('/company-references/')
  ) {
    return false;
  }

  // Safe read GET requests
  if (req.method === 'GET') {
    return true;
  }

  // Idempotent analytics reporting POST queries
  if (req.method === 'POST') {
    return CACHEABLE_POST_PATTERNS.some(p => req.url.includes(p));
  }

  return false;
}

function isInvalidatingRequest(req: HttpRequest<unknown>): boolean {
  if (req.method === 'POST' || req.method === 'PUT' || req.method === 'DELETE') {
    return INVALIDATION_PATTERNS.some(p => req.url.includes(p));
  }
  return false;
}

function buildCacheKey(req: HttpRequest<unknown>): string {
  const urlWithParams = req.urlWithParams;
  const method = req.method;

  if (method === 'POST') {
    if (req.body && typeof req.body === 'object') {
      const keys = Object.keys(req.body);
      if (keys.length > 0) {
        try {
          const bodyStr = JSON.stringify(req.body);
          return `${method}:${urlWithParams}:${bodyStr}`;
        } catch {
          return `${method}:${urlWithParams}`;
        }
      }
      return `${method}:${urlWithParams}`;
    } else if (req.body) {
      try {
        const bodyStr = JSON.stringify(req.body);
        return `${method}:${urlWithParams}:${bodyStr}`;
      } catch {
        return `${method}:${urlWithParams}`;
      }
    }
  }

  return `${method}:${urlWithParams}`;
}

function cloneResponseData<T>(data: T): T {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(data);
    } catch {
      // Fallback if data contains non-cloneable elements
    }
  }
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    return data;
  }
}

/**
 * Interceptor that records and retrieves API responses for each menu item
 * into the NgRx SignalStore (backed by IndexedDB).
 *
 * When navigating across sidebar menu items, previously fetched responses
 * are retrieved immediately from state, eliminating redundant API calls.
 */
export const menuDataCacheInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const menuDataStore = inject(MenuDataStore);
  const router = inject(Router);

  const currentRoute = normalizeMenuRoute(router.url);

  // If this is a mutation or manual refresh call, invalidate menu state
  if (isInvalidatingRequest(req)) {
    return next(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
          menuDataStore.invalidateMenu(currentRoute);
        }
      })
    );
  }

  // Check if this request is eligible for state management recording
  if (!isCacheable(req)) {
    return next(req);
  }

  const cacheKey = buildCacheKey(req);

  // Check if we already have valid recorded data in our NgRx SignalStore
  const cachedData = menuDataStore.getData(cacheKey);

  if (cachedData !== null && cachedData !== undefined) {
    // Return recorded response from NgRx state immediately; bypasses backend API call!
    return of(
      new HttpResponse({
        body: cloneResponseData(cachedData),
        status: 200,
        statusText: 'OK',
        url: req.urlWithParams
      })
    );
  }

  // Clean request header before sending to server if it was present
  let outgoingReq = req;
  if (req.headers.has(BYPASS_CACHE_HEADER)) {
    outgoingReq = req.clone({
      headers: req.headers.delete(BYPASS_CACHE_HEADER)
    });
  }

  // Pass through to network and record response in state management upon completion
  return next(outgoingReq).pipe(
    tap(event => {
      if (event instanceof HttpResponse && event.status === 200 && event.body !== undefined) {
        menuDataStore.recordData(currentRoute, cacheKey, cloneResponseData(event.body));
      }
    })
  );
};
